import {
  AfterViewInit,
  ApplicationRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  effect,
  inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Control, geoJSON, GeoJSON, LatLng, Layer, Map } from 'leaflet';
import { LeafletMapboxService } from '../../../services/leaflet-mapbox.service';
import { GlobalsStore } from '../../../stores/globals.state';
import { getState, patchState } from '@ngrx/signals';
import { LegendComponent } from '../legend/legend.component';
import { DataEntry, IndicatorData } from '../../../models/indicator-data';
import { Scale } from '../../../models/scale';
import { MapStore } from '../../../stores/map/map.state';
import { DataLayerTypeIcon } from '../../../stores/data-layer-type/data-layer-type.selectors';
import { DataLayerType } from '../../../models/data-layer-type';
import {
  ComparisonValue,
  Feature,
  FeatureCollection,
} from '../../../models/geo-data';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { GeoGroupDropdownComponent } from '../../atoms/geo-group-dropdown/geo-group-dropdown.component';
import { FormsModule } from '@angular/forms';
import { filterGeoJsonFeaturesByGroup } from '../../../stores/geo/geo-utils';
import { Colorset, fallbackColors } from '../../../models/colorset';
import { ComparisonValueStore } from '../../../stores/comparison-value/comparison-value.state';
import { Indicator } from '../../../models/indicator';
import { ColorsetStore } from '../../../stores/colorset/colorset.state';
import { AtlasConfiguration } from '../../../models/commune';
import { DataLayer } from '../../../models/data-layer';
import { MapRenderService } from '../../../services/map/map-render.service';
import { MapDataHelper } from '../../../services/map/map-data.helper';
import { MapTooltipService } from '../../../services/map/map-tooltip.service';
import { PoiIsochroneService } from '../../../services/isochrone/poi-isochrone.service';

@Component({
  selector: 'app-basic-map',
  standalone: true,
  imports: [GeoGroupDropdownComponent, FormsModule],
  templateUrl: './basic-map.component.html',
  styleUrl: './basic-map.component.scss',
  providers: [LeafletMapboxService, MapStore, PoiIsochroneService],
})
export class BasicMapComponent implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('mapContainer', { static: true }) mapContainer!: any;
  readonly mapStore = inject(MapStore);
  readonly layerTypeStore = inject(DataLayerTypeStore);
  readonly comparisonStore = inject(ComparisonValueStore);
  readonly colorsetStore = inject(ColorsetStore);
  readonly globalsStore = inject(GlobalsStore);
  readonly renderService = inject(MapRenderService);
  readonly dataHelper = inject(MapDataHelper);
  readonly tooltipService = inject(MapTooltipService);

  @Input() global = true;
  @Input() geoJsonData: FeatureCollection | null = null;
  @Input() dataLayerType: DataLayerType | null = null;
  @Input() showGlobalData = false;

  mapId = '';
  icon: DataLayerTypeIcon | null = null;
  selectedGroup = 'all';

  private comparisonValues: ComparisonValue[] = [];
  private indicator: Indicator | null = null;
  private layer: DataLayer | null = null;
  private indicatorData?: IndicatorData;
  private colorset?: Colorset;
  private colors?: string[];
  private geoJsonLayer!: GeoJSON;
  private currentLayer!: L.Layer;
  private legendComponentRef?: ComponentRef<LegendComponent>;
  private legendControl?: Control;
  private map!: Map;
  private atlasConfig?: AtlasConfiguration;

  constructor(
    private mapboxService: LeafletMapboxService,
    private resolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector
  ) {
    this.mapId = 'map_' + Date.now();
    effect(() => {
      const globalState = getState(this.globalsStore);

      this.atlasConfig =
        globalState.activeCommune?.atlasConfig ||
        this.globalsStore.getBaseConfig();
      const data = this.globalsStore.getActiveIndicatorData();

      if (!globalState.selectedLayer || !this.atlasConfig || !this.geoJsonData)
        return;
      if (
        this.indicator?._id !== globalState.selectedIndicator?._id ||
        this.layer?._id !== globalState.selectedLayer._id ||
        this.indicatorData?._id !== data?._id
      ) {
        this.indicator = globalState.selectedIndicator;
        this.layer = globalState.selectedLayer;
        this.indicatorData = data;
        this.comparisonValues = this.comparisonStore.asArray();

        setTimeout(() => this.renderMap(), 500);
      }
    });
  }

  /**
   * Lifecycle hook that is triggered after the component's view has been fully initialized.
   * Initializes the map by creating an instance using the provided mapId, renders the map
   * on the view, and sets the initial map layer style to 'light'.
   *
   * @return {void} Does not return any value.
   */
  ngAfterViewInit(): void {
    setTimeout(() => {
      this.map = this.mapboxService.initializeMap(
        this.mapContainer.nativeElement
      );
      this.mapStore.setMap(this.map, this.mapId);
      this.renderMap();
      this.changeLayer('light');
    }, 500);
  }

  /**
   * Lifecycle hook that is called when any data-bound property of a directive changes.
   * Handles changes to the input properties and updates the map rendering accordingly.
   *
   * @param {SimpleChanges} changes - Object of current and previous property values, indicating which inputs have changed.
   * @return {void} This method does not return any value.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['geoJsonData'] && this.geoJsonData && this.map) {
      this.renderMap();
    }
  }

  /**
   * Lifecycle hook that is called when the component or directive is destroyed.
   * This method performs cleanup tasks such as removing map layers, detaching event listeners,
   * and clearing the map instance to free resources.
   *
   * @return {void} Does not return a value.
   */
  ngOnDestroy(): void {
    if (this.map) {
      this.clearLayers();
      this.removeLegendComponent();
      this.map.remove();
      this.map.off();
      this.mapId = '';
    }
  }

  /**
   * Renders the map with the appropriate configuration and data layers.
   * The method sets the map configuration, retrieves the icon for a specific
   * data layer type if available, and updates the GeoJSON layer accordingly.
   *
   * @return {void} Does not return a value.
   */
  renderMap(): void {
    this.setMapConfig();
    if (this.dataLayerType && this.dataLayerType.icon) {
      this.layerTypeStore
        .getPoiIcon(this.dataLayerType._id)
        ?.subscribe(icon => {
          this.icon = icon;
          this.updateGeoJsonLayer();
        });
    } else {
      this.updateGeoJsonLayer();
    }
  }

  /**
   * Configures the map settings including color sets, scales, and data based on the provided
   * indicator, indicator data, atlas configuration, and map store state.
   *
   * @return {void} This method does not return any value.
   */
  setMapConfig(): void {
    const colorsetId =
      this.indicatorData?.colorSet ||
      this.indicator?.defaultColorSet ||
      this.atlasConfig?.map?.colorset ||
      this.atlasConfig?.colorset;
    this.colorset = colorsetId
      ? this.colorsetStore.getById(colorsetId)
      : undefined;
    const invert = !!(
      (this.indicator && this.indicator.defaultInvert) ||
      (this.indicatorData && this.indicatorData.invert)
    );

    const colors = this.colorset?.colors
      ? [...this.colorset.colors]
      : [...fallbackColors];
    this.colors = invert ? colors.reverse() : colors;

    const data = this.getGlobalDataEntry();

    const scales =
      this.indicatorData && this.indicator
        ? this.dataHelper.getScales(
            this.indicatorData,
            this.indicator,
            data,
            this.colors || fallbackColors,
            {
              legendScope: this.atlasConfig?.map?.legend?.legendScope,
              scaleMode: this.atlasConfig?.map?.legend.scaleMode,
              numberOfScales: this.atlasConfig?.map?.legend.numberOfScales,
            }
          )
        : undefined;

    patchState(this.mapStore, {
      colors: colors,
      scales: scales,
      indicator: this.indicator,
      data: data,
      comparisonValues: this.comparisonValues,
    });
  }

  /**
   * Retrieves the global data entry after removing comparison values based on the keys.
   *
   * The method accesses `indicatorData.data` and removes any entries corresponding to the keys
   * specified in `comparisonValues`. If `indicatorData.data` is unavailable, it returns undefined.
   *
   * @return {DataEntry|undefined} The filtered global data entry object, or undefined if no data is available.
   */
  getGlobalDataEntry(): DataEntry | undefined {
    if (!this.indicatorData?.data) return undefined;

    const filteredData = { ...this.indicatorData.data };

    // Remove comparison values
    for (const value of this.comparisonValues) {
      delete filteredData[value.key];
    }

    // Transform values to replace NaN with null
    for (const key of Object.keys(filteredData)) {
      const currentValue = filteredData[key];
      filteredData[key] = currentValue ?? null;
      //currentValue && isNaN(currentValue) ? null : currentValue || null;
    }

    return filteredData;
  }

  /**
   * Updates the GeoJSON layer with new data, styles, and colors.
   * This method checks if the current GeoJSON data is a valid FeatureCollection
   * and applies the updates accordingly using the provided global data entry
   * and color configuration.
   *
   * @return {void} Does not return a value.
   */
  private updateGeoJsonLayer(): void {
    const data = this.getGlobalDataEntry();

    if (this.geoJsonData && this.geoJsonData.type === 'FeatureCollection') {
      this.updateGeoLayer(
        this.geoJsonData,
        data,
        this.colors || fallbackColors
      );
    }
  }

  /**
   * Changes the current map layer to the specified layer type.
   *
   * @param {string} layerType - The type of layer to change to. Valid values are "light", "dark_mono",
   * "dark", "traffic", "streets", and "satellite". Each layer type corresponds to a specific style.
   * @return {void} This method does not return a value.
   */
  changeLayer(layerType: string): void {
    const styles: { [key: string]: string } = {
      light: 'assets/vector-layers/light.json',
      dark_mono: 'assets/vector-layers/dark_mono.json',
      dark: 'assets/vector-layers/dark.json',
      traffic: 'assets/vector-layers/traffic.json',
      streets: 'assets/vector-layers/streets.json',
      satellite: 'assets/vector-layers/satellite.json',
    };

    if (this.currentLayer) {
      this.mapboxService.clearLayers(this.map, this.currentLayer, true);
    }

    this.currentLayer = this.mapboxService.createMapboxGLLayer(layerType);
    this.currentLayer.addTo(this.map);
  }

  /**
   * Updates the GeoJSON layer on the map by removing the existing layer (if any) and adding a new one based on the provided GeoJSON data. Also adjusts the map view to fit the bounds of the new GeoJSON layer, and updates the legend.
   *
   * @param {FeatureCollection} geoJsonData - The GeoJSON data used to create or update the layer.
   * @param {DataEntry | undefined} data - Optional additional data used for styling the features in the layer.
   * @param {string[]} colors - Array of colors used for styling the map features and legend.
   * @return {void} This method does not return a value.
   */
  updateGeoLayer(
    geoJsonData: FeatureCollection,
    data: DataEntry | undefined,
    colors: string[]
  ): void {
    if (!this.map) return;
    if (this.geoJsonLayer) {
      this.map.removeLayer(this.geoJsonLayer);
    }
    const scales = this.mapStore.scales() || undefined;

    this.geoJsonLayer = geoJSON(
      filterGeoJsonFeaturesByGroup(geoJsonData, this.selectedGroup),
      {
        style: feature =>
          this.renderService.styleFn(feature, data, scales, colors),
        ...(this.icon
          ? {
              pointToLayer: (feature: Feature, latlng: LatLng) => {
                return this.renderService.createStyledMarker(
                  feature,
                  {
                    latlng,
                    data,
                    scales,
                    colors,
                    icon: this.icon,
                  },
                  colors
                ) as Layer;
              },
            }
          : {}),
        onEachFeature: (feature, layer) => {
          if (feature) {
            const popupContent = this.tooltipService.buildPoiPopup(
              this.dataLayerType?.name || '',
              feature,
              this.icon?.svg,
              undefined,
              this.indicator ? [this.indicator] : undefined,
              this.icon?.color
            );
            layer.bindPopup(popupContent);
          }
        },
      }
    ).addTo(this.map);

    const bounds = this.geoJsonLayer.getBounds();

    if (
      bounds.isValid() &&
      bounds.getSouthWest().equals(bounds.getNorthEast())
    ) {
      // Only one point is present
      this.map.setView(bounds.getCenter(), 15); // 15 = a reasonable zoom level, adjust as needed
    } else {
      this.map.fitBounds(bounds);
    }

    this.addLegendComponent(scales, colors);
  }

  /**
   * Clears all layers from the current map instance.
   *
   * This method removes and resets all layers added to the map, ensuring
   * no residual visual layers or data remain in the current map context.
   *
   * @return {void} No return value.
   */
  clearLayers(): void {
    this.mapboxService.clearLayers(this.map, undefined, true);
  }

  /**
   * Adds a legend component to the map with specified scales and colors.
   * This method removes any existing legend component before adding the new one.
   *
   * @param {Scale[] | undefined} scales - The array of scale objects to be displayed in the legend, or undefined if no scales are provided.
   * @param {string[]} colors - The array of colors corresponding to the scales.
   * @return {void} - Does not return a value.
   */
  private addLegendComponent(
    scales: Scale[] | undefined,
    colors: string[]
  ): void {
    this.removeLegendComponent();
    if (!scales) {
      return;
    }
    const factory = this.resolver.resolveComponentFactory(LegendComponent);
    this.legendComponentRef = factory.create(this.injector);

    this.legendComponentRef.instance.scales = scales;
    this.legendComponentRef.instance.colors = colors;
    if (this.indicator) {
      this.legendComponentRef.instance.indicator = this.indicator;
    }

    const comparisonData = this.getComparison();
    if (comparisonData)
      this.legendComponentRef.instance.comparisonData = comparisonData;

    this.appRef.attachView(this.legendComponentRef.hostView);
    const domElem = (this.legendComponentRef.hostView as any)
      .rootNodes[0] as HTMLElement;

    this.legendControl = new Control({ position: 'bottomright' });

    this.legendControl.onAdd = () => {
      return domElem;
    };

    this.legendControl.addTo(this.map);
  }

  /**
   * Removes the legend component from the UI and cleans up associated resources.
   *
   * This method detaches the legend component's view from the application, destroys the
   * component instance, and removes the legend control from the map, ensuring all references
   * are set too undefined to prevent memory leaks.
   *
   * @return {void} No return value.
   */
  removeLegendComponent(): void {
    if (this.legendComponentRef) {
      this.appRef.detachView(this.legendComponentRef.hostView);
      this.legendComponentRef.destroy();
      this.legendComponentRef = undefined;
    }
    if (this.legendControl) {
      this.map.removeControl(this.legendControl);
      this.legendControl = undefined;
    }
  }

  /**
   * Filters the data or map elements based on the provided group.
   *
   * @param {string} event - The identifier of the group to filter by.
   * @return {void} Does not return a value.
   */
  filterByGroup(event: string): void {
    this.selectedGroup = event;
    this.renderMap();
  }

  /**
   * Computes and retrieves a set of comparison values based on the current indicator and its associated data.
   * The returned object contains keys with associated numerical values, representing comparisons such as average, total, or other indicator-specific metrics.
   *
   * @return {null | Object<string, number>} An object mapping comparison keys to numerical values if data exists; otherwise, null.
   */
  getComparison(): null | { [key: string]: number } {
    if (!this.indicator || !this.indicatorData) return null;

    const compVals: { [key: string]: number } = {};
    if (this.indicatorData.avg) compVals['avg'] = this.indicatorData.avg;
    if (this.indicatorData.total) compVals['total'] = this.indicatorData.total;

    this.comparisonValues.forEach(comparison => {
      const value = this.indicatorData?.data[comparison.key];
      if (value) compVals[comparison.key] = value;
    });
    return Object.keys(compVals).length ? compVals : null;
  }
}
